import React, { useState, useEffect } from "react";
import { SlArrowRight } from "react-icons/sl";
import ApplicationDetailsModal from "../components/ApplicantDetailsModal.js";
import { IoSearch } from "react-icons/io5";
import apiClient from "../api/axiosInstance.js";
import { formatDate } from "../utilities/formatDate.js";

const ApplicationsTable = ({ activeNav }) => {
  const [applications, setApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [archivedFilter, setArchivedFilter] = useState(false);
  const [pendingFilter, setPendingFilter] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortColumn, setSortColumn] = useState("");
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [approvedFilter, setApprovedFilter] = useState(false);

  const fetchApplications = async () => {
    try {
      const response = await apiClient.get("/api/all-applications");
      // Sort by createdAt date in descending order (latest applications first)
      const sortedApplications = response.data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setApplications(sortedApplications);
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  // Setting the filter based on the active navigation item
  useEffect(() => {
    if (activeNav === "Pending Applications") {
      setPendingFilter(true);
      setApprovedFilter(false);
      setArchivedFilter(false);
    } else if (activeNav === "Approved Applications") {
      setPendingFilter(false);
      setApprovedFilter(true);
      setArchivedFilter(false);
    } else if (activeNav === "Archived Applications") {
      setPendingFilter(false);
      setApprovedFilter(false);
      setArchivedFilter(true);
    } else {
      // Reset filters if it's any other tab
      setPendingFilter(false);
      setApprovedFilter(false);
      setArchivedFilter(false);
    }
  }, [activeNav]);

  // Filter and sort the applications
  const filteredApplications = applications
    .filter((application) => {
      const fullName =
        `${application.surname} ${application.firstName} ${application.otherNames}`.toLowerCase();
      const matchesSearchTerm = fullName.includes(searchTerm.toLowerCase());

      // Filter based on checkbox selection
      let matchesStatus = true; // Default to true to include all if no checkbox is checked
      if (pendingFilter) {
        matchesStatus = application.applicationStatus === "Pending";
      }
      if (approvedFilter) {
        matchesStatus = application.applicationStatus === "Approved";
      }
      if (archivedFilter) {
        matchesStatus = application.applicationStatus === "Archived";
      }

      return matchesSearchTerm && matchesStatus;
    })
    .sort((a, b) => {
      if (sortColumn === "applicationDate") {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      }
      return 0;
    });

  // Handle sorting logic
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Handle opening the modal with the selected applicant's data
  const handleViewClick = (applicant) => {
    setSelectedApplicant(applicant);
    setIsModalOpen(true); // Open the modal
  };

  return (
    <div className="w-full bg-white border-b-2 shadow-lg px-2 lg:pr-5 pb-4 rounded-md overflow-x-auto h-[calc(100vh-14.2rem)]">
      <div className="flex flex-col sm:flex-row justify-between lg:items-center mb-6">
        <div className="flex">
          <SlArrowRight className="mt-2 lg:ml-4" />
          <h2 className="text-lg sm:text-xl ml-4 font-semibold">{activeNav}</h2>
        </div>

        <div className="relative w-full sm:w-1/3 mt-4 sm:mt-0">
          <input
            type="text"
            className="border border-green-700 rounded-xl p-2 pl-14 w-full"
            placeholder="Search for Applicant to extend"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <IoSearch className="absolute left-5 top-3 text-green-700 text-xl" />
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-4 mt-4 sm:mt-0">
          <label className="flex items-center text-sm sm:text-base">
            <input
              type="checkbox"
              className="mr-2"
              checked={pendingFilter}
              onChange={() => setPendingFilter(!pendingFilter)}
            />
            Pending Applications
          </label>
          <label className="flex items-center text-sm sm:text-base">
            <input
              type="checkbox"
              className="mr-2"
              checked={approvedFilter}
              onChange={() => setApprovedFilter(!approvedFilter)}
            />
            Approved Applications
          </label>
          <label className="flex items-center text-sm sm:text-base">
            <input
              type="checkbox"
              className="mr-2"
              checked={archivedFilter}
              onChange={() => setArchivedFilter(!archivedFilter)}
            />
            Archived Applications
          </label>
        </div>
      </div>

      <table className="w-full table-auto  border-2 border-green-700 text-left border-collapse rounded-t-3xl lg:overflow-hidden">
        <thead className="border-b border-green-700">
          <tr className="bg-green-700 bg-opacity-85 text-white">
            <th className="p-2 lg:p-5 text-sm sm:text-base font-semibold min-w-[50px]">
              #
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[100px] max-w-[200px]">
              Applicant Name
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[80px] max-w-[150px]">
              Department
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[100px] max-w-[150px]">
              Phone Number
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[80px] max-w-[120px]">
              Nationality
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[100px] max-w-[150px]">
              Internship Start Date
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[100px] max-w-[150px]">
              Internship End Date
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[80px] max-w-[90px] ">
              Application Status
            </th>
            <th
              className="p-2 text-sm sm:text-base font-semibold min-w-[100px] max-w-[150px] cursor-pointer"
              onClick={() => handleSort("applicationDate")}
            >
              Application Date{" "}
              {sortColumn === "applicationDate" &&
                (sortOrder === "asc" ? "🔼" : "🔽")}
            </th>
            <th className="p-2 text-sm sm:text-base font-semibold min-w-[50px] ">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredApplications.map((applicant, index) => (
            <tr
              key={index}
              className={`hover:bg-gray-300 transition duration-200 cursor-pointer ${
                index % 2 === 0
                  ? "bg-yellow-50 bg-opacity-50"
                  : "bg-gray-200 bg-opacity-50"
              }`}
              onClick={() => handleViewClick(applicant)}
            >
              <td className="px-2 py-2 lg:py-3 border-b border-gray-300 font-medium text-sm lg:text-[15px] text-center">
                {index + 1}.
              </td>
              <td className="px-2 py-2 lg:py-3 border-b border-gray-300 font-medium text-sm lg:text-[15px] truncate">{`${applicant.surname} ${applicant.firstName} ${applicant.otherNames}`}</td>
              <td className="px-2 py-2 lg:py-3 border-b border-gray-300 font-medium text-sm lg:text-[15px] truncate">
                {applicant.internshipDepartment}
              </td>
              <td className="px-2 py-2 lg:py-3 border-b border-gray-300 text-sm lg:text-base">
                {applicant.phoneNumber}
              </td>
              <td className="px-2 py-2 lg:py-3 border-b border-gray-300 text-sm lg:text-[15px] truncate">
                {applicant.nationality}
              </td>
              <td className="px-2 py-2 lg:py-3 border-b border-gray-300 text-sm lg:text-[15px]">
                {formatDate(applicant.internshipStartDate)}
              </td>
              <td className="px-2 py-2 lg:py-3 border-b border-gray-300 text-sm lg:text-[15px]">
                {formatDate(applicant.internshipEndDate)}
              </td>
              <td className="px-2 py-2 lg:py-3 border-b border-gray-300 text-sm lg:text-[15px] font-semibold">
                {applicant.applicationStatus}
              </td>
              <td className="px-2 py-2 lg:py-3 border-b border-gray-300 text-sm lg:text-[15px]">
                {formatDate(applicant.createdAt)}
              </td>
              <td className="px-2 py-2 lg:py-3 border-b border-gray-300 text-sm lg:text-[15px]">
                <button
                  className="bg-green-700 text-white py-[6px] px-5 lg:px-8 rounded-lg text-xs lg:text-sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedApplicant(applicant);
                    setIsModalOpen(true);
                  }}
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ApplicationDetailsModal
        isModalOpen={isModalOpen}
        selectedApplicant={selectedApplicant}
        setIsModalOpen={setIsModalOpen}
      />
    </div>
  );
};

export default ApplicationsTable;
